import React, { type CSSProperties, forwardRef, type ReactElement } from 'react';

import { FixedHolder } from '../FixedHolder';
import { type TableProps } from '../Table';
import {
    type ColsWidths,
    type ColumnConfig,
    type ExpandableConfig,
    type Key,
    type TableResourceRecord
} from '../Table.types';

import MeasureRow from './MeasureRow';
import { TableRow } from './TableRow';

import styles from '../Table.module.scss';

type TableBodyProps<RecordType = any> = {
    createRowKey: (row: RecordType, index: number) => Key;
    expandedKeys: Set<Key>;
    isTableExpandable: boolean;
    columns: ColumnConfig<RecordType>[];
    withInnerScroll?: boolean;
    onColumnsResize: (value: Map<Key, number>) => void;
    onRowsResize?: (width: number) => void;
    colsWidths: ColsWidths;
    columnsKey: Key[];
} & Pick<TableProps<RecordType>, 'height' | 'maxHeight' | 'dataSource'> &
    Pick<ExpandableConfig<RecordType>, 'rowExpandable' | 'expandedRowRender'>;

export const TableBody = forwardRef<HTMLDivElement, TableBodyProps>(
    <RecordType extends TableResourceRecord = any>(
        {
            height,
            maxHeight,
            columns,
            dataSource,
            createRowKey,
            expandedRowRender,
            rowExpandable,
            isTableExpandable,
            expandedKeys,
            withInnerScroll,
            onColumnsResize,
            onRowsResize,
            colsWidths,
            columnsKey
        }: TableBodyProps<RecordType>,
        ref: React.Ref<HTMLDivElement>
    ): ReactElement => {
        const containerStyles: CSSProperties = {};
        const tableStyles: CSSProperties = {};
        if (height || maxHeight) {
            containerStyles.height = height ?? 'auto';
            containerStyles.maxHeight = maxHeight ?? 'auto';
            containerStyles.overflowY = 'auto';
        }
        if (withInnerScroll) {
            tableStyles.tableLayout = withInnerScroll ? 'fixed' : 'auto';
        }

        const tableBody = (
            <tbody>
                {withInnerScroll && (
                    <MeasureRow
                        columnsKey={columnsKey}
                        onColumnsResize={onColumnsResize}
                        onRowResize={onRowsResize}
                    />
                )}
                {dataSource?.map((row, index) => {
                    const key = createRowKey(row, index) as string;
                    const isExpanded = expandedKeys.has(key);
                    return (
                        <TableRow
                            key={key}
                            rowKey={key}
                            row={row}
                            isExpanded={isExpanded}
                            expandedRowRender={expandedRowRender}
                            rowExpandable={rowExpandable}
                            isExpandable={isTableExpandable}
                            cols={columns}
                            index={index}
                        />
                    );
                })}
            </tbody>
        );

        return withInnerScroll ? (
            <FixedHolder
                className={styles.tableBody}
                style={containerStyles}
                tableStyle={tableStyles}
                ref={ref}
                columns={columns}
                colsWidths={colsWidths}
            >
                {tableBody}
            </FixedHolder>
        ) : (
            tableBody
        );
    }
);

TableBody.displayName = 'TableBody';
